import { useState } from 'react';
import { useSettings } from './hooks/useApi';
import QueueView from './components/QueueView';
import HistoryView from './components/HistoryView';
import MetricsView from './components/MetricsView';
import SettingsView from './components/SettingsView';

type Tab = 'queue' | 'history' | 'metrics' | 'settings';

const TABS: { id: Tab; label: string }[] = [
  { id: 'queue', label: 'Queue' },
  { id: 'history', label: 'History' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'settings', label: 'Settings' },
];

export default function App() {
  const [activeTab, setActiveTab] = useState<Tab>('queue');
  const { data: settings, isError: settingsError } = useSettings();

  const systemOnline = !settingsError && settings !== undefined;
  const killSwitchActive = settings?.kill_switch ?? false;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">GHW Email Intelligence</h1>
            <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${
                  !systemOnline
                    ? 'bg-red-500'
                    : killSwitchActive
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                }`}
              />
              {!systemOnline ? 'Offline' : killSwitchActive ? 'Paused' : 'Online'}
            </span>
          </div>
        </div>

        {/* Tab bar */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex gap-6" aria-label="Tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap border-b-2 pb-3 pt-1 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {activeTab === 'queue' && <QueueView />}
        {activeTab === 'history' && <HistoryView />}
        {activeTab === 'metrics' && <MetricsView />}
        {activeTab === 'settings' && <SettingsView />}
      </main>
    </div>
  );
}
